import { pickBy } from 'lodash-es'
import type {
  Media,
  Task,
  TaskModel,
} from 'src/services/apollo/generated/graphql'
import { getCompressedDimensions } from 'src/utils/imageUtil'
import {
  getPrimaryMediaIdFromTask,
  isI2vProTask,
  isReferenceVideoTask,
  isT2I2VTask,
  isWorkflowTask,
} from 'src/utils/task'
import { UnFile } from 'src/utils/unFile'
import type {
  ControlNetItem,
  TaskSubmitFormValues,
} from '../BaseTaskSubmitForm'
import { defaultTaskFormValues, samplingMethodChoices } from '../constants'
import type { NoteContentData } from './Generation/RightCol/TaskNotes'

const FLOOR = 8

const DEFAULT_SCALE_TO = 1344
const MAX_UPSCALE_TO = 2048
const MAX_ENLARGE_TO = 2688

/**
 * Max integer divisible by 8
 * @param length
 * @param ratio
 * @returns
 */
export function getFlooredValue(length: number, ratio: number) {
  return Math.floor((length * ratio) / FLOOR) * FLOOR
}

/**
 * Upscale/Hires: longest dimension <= 2048
 * @param param0
 * @returns
 */
export function getMaxUpscaleRatio({
  width,
  height,
}: {
  width?: number
  height?: number
}): number | undefined {
  if (width == null || height == null) return undefined
  const maxUpscaleTo = MAX_UPSCALE_TO

  return (
    Math.floor(Math.min(maxUpscaleTo / width, maxUpscaleTo / height) * 10) / 10
  )
}

/**
 * Upscale/Hires: longest dimension <= 2048
 * @param param0
 * @returns
 */
export function getDefaultUpscaleRatio({
  width,
  height,
}: {
  width?: number
  height?: number
}): number | undefined {
  if (width == null || height == null) return undefined

  const defaultUpscaleTo = DEFAULT_SCALE_TO

  const defaultRatio =
    Math.floor(
      Math.min(defaultUpscaleTo / width, defaultUpscaleTo / height) * 10,
    ) / 10

  const maxUpscaleRatio = getMaxUpscaleRatio({ width, height })

  if (!maxUpscaleRatio) return undefined
  const returnRatio = Math.min(defaultRatio, maxUpscaleRatio)

  // prevent downscaling
  return returnRatio < 1.1 ? 1.1 : returnRatio
}

/**
 * Enlarge: longest dimension <= 2688
 * @param param0
 * @returns
 */
export function getMaxEnlargableRatio({
  width,
  height,
}: {
  width?: number
  height?: number
}): number | undefined {
  if (width == null || height == null) return undefined
  const maxEnlargeTo = MAX_ENLARGE_TO

  return (
    Math.floor(Math.min(maxEnlargeTo / width, maxEnlargeTo / height) * 10) / 10
  )
}

/**
 * Enlarge: longest dimension <= 2688
 * @param param0
 * @returns
 */
export function getDefaultEnlargeRatio({
  width,
  height,
}: {
  width?: number
  height?: number
}): number | undefined {
  if (width == null || height == null) return undefined

  const defaultEnlargeTo = DEFAULT_SCALE_TO

  const defaultRatio =
    Math.floor(
      Math.min(defaultEnlargeTo / width, defaultEnlargeTo / height) * 10,
    ) / 10

  const maxEnlargeRatio = getMaxEnlargableRatio({ width, height })

  if (!maxEnlargeRatio) return undefined
  const returnRatio = Math.min(defaultRatio, maxEnlargeRatio)

  // prevent downscaling
  return returnRatio < 1.1 ? 1.1 : returnRatio
}

export const parseInitialValuesForNote = ({
  media,
  noteContent,
  defaultValues = defaultTaskFormValues,
  taskModelList = [],
  samplingMethods = samplingMethodChoices,
  extraInitialValues = {},
}: {
  media?: Media
  noteContent?: NoteContentData
  defaultValues?: TaskSubmitFormValues
  taskModelList?: TaskModel[]
  samplingMethods?: string[]
  extraInitialValues?: TaskSubmitFormValues
}): TaskSubmitFormValues => {
  return {
    ...defaultValues,

    // i2i params
    ...(media
      ? {
          strength: 0.7,
          baseImage: UnFile.fromMedia(media),
          ...getCompressedDimensions(media),
          size: undefined, // remove selected size
        }
      : {}),

    // ensure artworks' model and sampling method are valid
    ...pickBy(noteContent, (val, key) => {
      if (key === 'samplingMethod' && !samplingMethods.includes(val as string))
        return false
      if (
        key === 'model' &&
        !taskModelList.map(t => t.model).includes(val as string)
      )
        return false
      if (val != null) return true
    }),
    ...extraInitialValues,
  }
}

export const taskHasbatchImage = (task?: Task): boolean => {
  return task?.parameters.batchSize > 1
}

export type GeneratedItemId = {
  taskId: string
  taskType?: 'workflow'
  mediaId?: string
  deleted?: boolean
  bookmarked?: boolean
}

export const getTaskAndMediaIds = (
  task?: Partial<Task> & { id: string },
): GeneratedItemId[] | undefined => {
  if (!task) return undefined
  if (isI2vProTask(task) || isReferenceVideoTask(task) || isT2I2VTask(task)) {
    return [
      {
        taskId: task.id,
        mediaId: getPrimaryMediaIdFromTask(task),
        bookmarked: !!task.favoritedAt,
      },
    ]
  }

  if (task.parameters.batchSize > 1) {
    // task is not completed
    if (!task.outputs.batch) {
      return Array.from({ length: task.parameters.batchSize }).map(() => ({
        taskId: task.id,
        mediaId: undefined,
      }))
    }

    const batchOutput = (
      (task?.outputs.batch as TaskOutputBatchItem[]) ?? []
    ).map(o => ({
      mediaId: o.mediaId,
      taskId: task.id,
      deleted: !!o.deletedAt,
      bookmarked: !!o.favoritedAt,
    }))

    // 1 task + 3 variations
    if (task.parameters.batchSize === 3 && task.parameters.mediaId) {
      // add original task's mediaId as first image
      return [
        { taskId: task.id, mediaId: task.parameters.mediaId },
        ...batchOutput,
      ]
    }

    return batchOutput
  }
  if (isWorkflowTask(task)) {
    return [
      {
        taskId: task.id,
        taskType: 'workflow',
        mediaId: task.outputs.batch?.[0]?.mediaId || task.outputs.mediaId,
      },
    ]
  }
  return [
    {
      taskId: task.id,
      mediaId: getPrimaryMediaIdFromTask(task),
      bookmarked: !!task.favoritedAt,
    },
  ]
}

export const getNewGeneratedItems = (
  prevGeneratedItems: GeneratedItemId[],
  task: Task, // completed task
  // itemIdx: number,
): GeneratedItemId[] => {
  if (prevGeneratedItems.length === 1) {
    // single gen
    return [{ taskId: task.id, mediaId: task.mediaId }]
  }
  if (prevGeneratedItems.length === 4) {
    return getTaskAndMediaIds(task) ?? []
  }
  return [] // others are unsupported
}

export const getTaskDimensionsForImport = (
  task?: Partial<Task>,
  baseMedia?: Media,
): {
  width?: number
  height?: number
} => {
  if (!task)
    return {
      width: undefined,
      height: undefined,
    }

  // i2i tasks use base image's size
  if (baseMedia) {
    return {
      width: baseMedia.width,
      height: baseMedia.height,
    }
  }

  /**
   * Situations where task.parameters has incorrect sizes:
   * 1. older / problemetic tasks didn't provide size on submit
   */
  if (!task.parameters?.width || !task.parameters?.height) {
    /**
     * Situations where task.media has "incorrect" sizes:
     * 1. awaiting t2i tasks: width / height are undefined
     * 2. upscaled i2i tasks: width / height are upscaled
     * 3. hires t2i tasks: width / height are upscaled
     * 4. batch i2i tasks: width / height are 2x
     */
    return {
      width: task.media?.width,
      height: task.media?.height,
    }
  }

  return {
    width: task.parameters?.width,
    height: task.parameters?.height,
  }
}

export const isMediaFaved = (task: Task, mediaId?: string) => {
  if (!mediaId) return false
  return (
    (task?.outputs.batch ?? []) as {
      seed: string
      mediaId: string
      deletedAt?: string
      favoritedAt?: string
    }[]
  ).some(o => o.mediaId === mediaId && !!o.favoritedAt)
}

export const isTaskGenerativeFill = (task: Task) => {
  if (!task) return false
  return ((task.parameters.controlNets as ControlNetItem[]) ?? []).some(
    c => c.type === 'inpaint',
  )
}

/**
 * Which position is this media in batch
 * For variation's base task, the mediaIdx is undefined
 */
export const getMediaIdx = (task?: Task, mediaId?: string) => {
  if (!mediaId) return undefined
  if (!Array.isArray(task?.outputs.batch)) return undefined
  const idx = (
    (task?.outputs?.batch as { seed: string; mediaId: string }[]) ?? []
  ).findIndex(b => b.mediaId === mediaId)
  if (idx < 0) return undefined
  return idx
}

export const updatePromptsWithTriggerWords = ({
  prompts,
  wordsToAppend,
  wordsToDelete,
}: {
  prompts: string | undefined
  wordsToAppend: string[]
  wordsToDelete: string[]
}) => {
  let updatedPrompts = prompts ?? ''
  if (wordsToDelete.length > 0) {
    wordsToDelete.forEach(word => {
      updatedPrompts = updatedPrompts.replaceAll(word, '')
    })
  }
  if (wordsToAppend.length > 0) {
    updatedPrompts = [updatedPrompts, wordsToAppend.join(',')]
      .filter(v => !!v)
      .join(',')
  }
  // remove duplicated commas
  return updatedPrompts.replaceAll(/,+/g, ',')
}

export type TaskOutputBatchItem = {
  seed: string
  mediaId: string
  deletedAt?: string
  favoritedAt?: string
}
